import { X } from 'lucide-react';
import { CHAT_SURFACE } from '@/config/environment';
import { CALLOUT_UNREAD_TEXT, calloutTextFor } from './badgerCallout';
import { useCentralChatRuntime } from './centralChatRuntime';
import { EmjaeMascot } from './EmjaeMascot';
import {
  IDLE_CYCLES_BEFORE_WAVE,
  headerPose,
  replyHasText,
  useEmjaeMascotMotion,
  useIdleCycle,
} from './emjaeMascotMotion';
import { chatSurfaceFor } from './railRoutes';
import { useBadgerCallout } from './useBadgerCallout';

interface CentralChatHeaderLauncherProps {
  pathname: string;
  /** Overrides the build-time VITE_CHAT_SURFACE value (tests). */
  surface?: unknown;
}

/**
 * The Emjae badger in the top bar, next to the notification bell. Clicking it opens the side
 * panel (CentralChatHeaderPanel) and clicking it again closes it. Its speech bubble (the
 * page line, the unread note) hangs below it.
 *
 * It gates itself: nothing unless the surface for this page is the header, so the layout can
 * always mount it and bubble / rail mode, the editor and /chat get no badger in the bar. The
 * gate is a separate component so that none of the call-out timers run when it is not shown.
 */
export function CentralChatHeaderLauncher({ pathname, surface }: CentralChatHeaderLauncherProps) {
  if (chatSurfaceFor(pathname, surface ?? CHAT_SURFACE) !== 'header') return null;
  return <HeaderBadger pathname={pathname} />;
}

function HeaderBadger({ pathname }: { pathname: string }) {
  const chat = useCentralChatRuntime();
  const { bubbleOpen, unreadCount } = chat;
  const callout = useBadgerCallout({ bubbleOpen, unreadCount });
  const { calloutId, calloutVisible, live, wave, hop } = callout;
  // The same turn poses as the mascot above the message box. The call-out wave, the unread hop and
  // the reply's happy keep priority over the gentler idle cycle (wave about every 20 s), which rests
  // while the chat panel is open (the mascot above the message box is the one that moves then).
  const motion = useEmjaeMascotMotion({
    isStreaming: chat.isStreaming,
    messages: chat.messages,
    bubbleOpen,
    openNonce: chat.openNonce,
  });
  const basePose = headerPose({
    isStreaming: chat.isStreaming,
    toolRunning: chat.toolActivity.some((entry) => entry.status === 'running'),
    replyStarted: replyHasText(chat.messages),
    happy: hop || motion.happy,
    wave,
  });
  const cycle = useIdleCycle(basePose === 'idle' && !bubbleOpen, IDLE_CYCLES_BEFORE_WAVE.badger);
  const pose = cycle.waving ? 'wave' : basePose;
  const text = callout.unreadNote ? CALLOUT_UNREAD_TEXT : calloutTextFor(pathname);

  // No overflow-* anywhere on this wrapper: the top bar must not become a scrollport, and a
  // clipping box would cut the call-out off.
  return (
    <div className="relative flex items-center" onFocus={callout.onFocusWithin} onBlur={callout.onBlurWithin}>
      <button
        type="button"
        data-testid="header-badger"
        aria-label={unreadCount > 0 ? `Open Emjae, ${unreadCount} unread` : 'Open Emjae'}
        aria-expanded={bubbleOpen}
        aria-describedby={calloutVisible && !live ? calloutId : undefined}
        onClick={() => (bubbleOpen ? chat.closeBubble() : chat.openBubble())}
        onMouseEnter={() => callout.setHover(true)}
        onMouseLeave={() => callout.setHover(false)}
        className="relative grid size-10 place-items-center rounded-full transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
      >
        <EmjaeMascot
          size="header"
          px={36}
          className="size-9"
          pose={pose}
          motion={[hop && 'bounce', wave && 'wave']}
          onAnimationIteration={cycle.onIteration}
          onAnimationEnd={() => {
            callout.onMascotAnimationEnd();
            motion.clearHappy();
            cycle.onEnd();
          }}
        />
        {unreadCount > 0 && (
          <span
            data-testid="badger-unread"
            className="absolute -right-0.5 -top-0.5 grid min-w-5 place-items-center rounded-full bg-primary px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {calloutVisible && !bubbleOpen && (
        <div
          id={calloutId}
          data-testid="badger-callout"
          role={live ? 'status' : undefined}
          className="absolute right-0 top-full z-50 mt-2.5 flex w-max max-w-[min(220px,calc(100vw-32px))] items-start gap-1.5 rounded-xl border border-warm-border bg-paper py-2 pl-3 pr-1.5 shadow-lg"
        >
          <span className="min-w-0 break-words text-[13px] leading-snug text-ink">{text}</span>
          <button
            type="button"
            data-testid="badger-callout-dismiss"
            aria-label="Dismiss"
            onClick={callout.dismiss}
            className="grid size-5 shrink-0 place-items-center rounded-full text-ink-3 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
          {/* The tail, pointing up at the badger. */}
          <span
            aria-hidden="true"
            className="absolute -top-1.5 right-4 size-3 rotate-45 border-l border-t border-warm-border bg-paper"
          />
        </div>
      )}
    </div>
  );
}
